/**
 * What a training page shows where the live call shows "Audio Settings",
 * built to the same structure as that panel: a heading-4 title with the
 * small-button row on the right (where the call has Watch a session /
 * Connect Bot), and underneath the same dot-and-caption status lines. Page
 * details go in `children`: short ones as extra status lines (see
 * TrainingStatusLine), a longer briefing as labelled sections below them (see
 * PlaybookLiveBriefing), never new cards: the rep should see the live-call
 * panel with other button labels.
 *
 * "Start training" opens the headphone check as a modal; its "Start session"
 * starts the session.
 */
import React, { useState } from 'react';

import { TRAINING_CALL_COPY } from '../../constants/training';
import type { TurnState } from '../../utils/turnIndicatorState';
import EchoCheckCard from '../playbookLive/EchoCheckCard';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter } from '../ui/dialog';
import TrainingStatusLine from './TrainingStatusLine';

export type TrainingCallPhase = 'preflight' | 'connecting' | 'live' | 'completed';

interface TrainingStatusPanelProps {
  phase: TrainingCallPhase;
  onStart: () => void;
  onEnd: () => void;
  isEnding: boolean;
  /** Drives the turn line while live (useTurnIndicator). */
  turn?: TurnState;
  /** e.g. "1 of 3 answered"; shown as a status line once known. */
  progressLabel?: string;
  /** Extra TrainingStatusLine rows. */
  children?: React.ReactNode;
}

/** The status line outside the live phase; while live the turn pill is the status. */
type NotLivePhase = Exclude<TrainingCallPhase, 'live'>;

const STATUS_LABEL: Record<NotLivePhase, string> = {
  preflight: TRAINING_CALL_COPY.inactive,
  connecting: TRAINING_CALL_COPY.connecting,
  completed: TRAINING_CALL_COPY.ended,
};

const STATUS_DOT: Record<NotLivePhase, string> = {
  preflight: 'bg-ink-muted',
  connecting: 'bg-signature animate-pulse',
  completed: 'bg-ink-muted',
};

const TURN_LABEL: Record<TurnState, string> = {
  ai_speaking: TRAINING_CALL_COPY.customerSpeaking,
  your_turn: TRAINING_CALL_COPY.repTurn,
  processing: TRAINING_CALL_COPY.processing,
  slow: TRAINING_CALL_COPY.processingSlow,
  stalled: TRAINING_CALL_COPY.processingStalled,
};

/**
 * A bold pill per turn state: solid signature while the customer talks,
 * solid success on the rep's turn, solid info while waiting. The slow states
 * use the warning tint with ink text and a warning border, because white on
 * the warning fill is too faint at this size.
 */
const TURN_PILL_BASE = 'inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold';
const TURN_PILL: Record<TurnState, string> = {
  ai_speaking: 'bg-signature text-on-signature',
  your_turn: 'bg-success text-on-signature',
  processing: 'bg-info text-on-signature animate-pulse',
  slow: 'bg-warning-tint text-ink border border-warning',
  stalled: 'bg-warning-tint text-ink border border-warning',
};

export const TrainingStatusPanel: React.FC<TrainingStatusPanelProps> = ({
  phase,
  onStart,
  onEnd,
  isEnding,
  turn = 'your_turn',
  progressLabel,
  children,
}) => {
  const [isAudioCheckOpen, setIsAudioCheckOpen] = useState(false);

  const handleReady = (): void => {
    setIsAudioCheckOpen(false);
    onStart();
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-1" data-testid="training-status-title-row">
        <div className="flex items-center gap-3 min-w-0">
          <h3 className="heading-4 shrink-0">{TRAINING_CALL_COPY.title}</h3>
          {phase === 'live' && (
            // On the title row, not a row of its own: the playbook panel
            // below gets the height back (owner, 2026-09-26). Its own polite
            // live region: the text changes only on a turn change, so a
            // screen reader announces each turn once.
            <div aria-live="polite" data-testid="training-turn" className="min-w-0">
              <span className={`${TURN_PILL_BASE} ${TURN_PILL[turn]} whitespace-nowrap`}>{TURN_LABEL[turn]}</span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 space-x-2">
          {phase === 'preflight' && (
            <Button variant="primary" size="sm" onClick={() => setIsAudioCheckOpen(true)}>
              {TRAINING_CALL_COPY.start}
            </Button>
          )}
          {/* Until the customer's first word: the rep must see that something
              is happening, and must not be offered End for a call that has
              not started. */}
          {phase === 'connecting' && (
            <Button variant="primary" size="sm" disabled aria-busy="true" data-testid="training-connecting">
              <span
                className="w-3.5 h-3.5 border-2 border-on-signature border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              />
              {TRAINING_CALL_COPY.connectingButton}
            </Button>
          )}
          {phase === 'live' && (
            <Button variant="destructive" size="sm" onClick={onEnd} disabled={isEnding}>
              {isEnding ? TRAINING_CALL_COPY.ending : TRAINING_CALL_COPY.end}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex flex-col w-full min-w-0" role="status">
          {/* While live the turn pill on the title row IS the status. */}
          {phase !== 'live' && (
            <TrainingStatusLine dotClassName={STATUS_DOT[phase]}>{STATUS_LABEL[phase]}</TrainingStatusLine>
          )}
          {progressLabel && (
            <TrainingStatusLine dotClassName="bg-signature" testId="training-progress">
              {progressLabel}
            </TrainingStatusLine>
          )}
          {children}
        </div>
      </div>

      <Dialog open={isAudioCheckOpen} onOpenChange={setIsAudioCheckOpen}>
        <DialogContent>
          <EchoCheckCard onReady={handleReady} embedded />
          <DialogFooter>
            <Button variant="secondary" size="sm" onClick={() => setIsAudioCheckOpen(false)}>
              {TRAINING_CALL_COPY.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TrainingStatusPanel;
